import React from "react";
import { InputLabel } from "../../../../shared/InputLabel/InputLabel";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { Input } from "../../../../shared/Input/Input";
import { InputError } from "../../../../shared/InputError/InputError";
import { Button } from "../../../../shared/Button/Button";
import styles from "./styles.module.scss";
import { useForm } from "../../hooks/useForm";
import { useSubmit } from "../../hooks/useSubmit";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import { InputPassword } from "../../../../boxes/InputPassword/InputPassword";
import { InfoIcon } from "../../../../assets/Icons/InfoIcon";
import { ModalInfo } from "../../../../boxes/ModalInfo/ModalInfo";
import { PASS_CODE_LENGTH } from "../../../../schemas/passcode";
import { PasswordRequirements } from "./shared/PasswordRequirements/PasswordRequirements";
import { OTP_LENGTH } from "../../../../schemas/otp";

/**
 * @param {{
 *    wpuId: string | number;
 *    resendOtp: () => Promise<Object>;
 *    onSubmitSuccess: () => void;
 *    checkAuthBeforeSubmit?: boolean;
 *    requirePasscode?: boolean;
 * }} param0
 */
export function Form({
  wpuId,
  resendOtp,
  onSubmitSuccess,
  checkAuthBeforeSubmit,
  requirePasscode,
}) {
  var { tCommon: t } = useAppTranslation.Common();
  var [isPassCodeTooltipOpen, setIsPassCodeTooltipOpen] = React.useState(false);

  var submit = useSubmit({
    wpuId,
    onSuccess: onSubmitSuccess,
    checkAuthBeforeSubmit,
  });

  var { values, handleSubmit, handleBlur, handleChange, getError } = useForm({
    submitter: submit.initiate,
    requirePasscode,
  });

  var oldPasswordError = getError("oldPassword");
  var newPasswordError = getError("newPassword");
  var newPasswordConfirmError = getError("newPasswordConfirm");
  var otpError = getError("otp");
  var passCodeError = getError("passCode");

  return (
    <div className={styles.root}>
      <div>
        <InputLabel>{t("changePassword.otp")}</InputLabel>
        <Input
          name="otp"
          maxLength={OTP_LENGTH}
          value={values.otp}
          onChange={handleChange}
          onBlur={handleBlur}
          isError={!!otpError}
        />
        {!!otpError && <InputError>{otpError}</InputError>}
      </div>
      <div>
        <InputLabel>{t("changePassword.oldPassword")}</InputLabel>
        <InputPassword
          name="oldPassword"
          value={values.oldPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          isError={!!oldPasswordError}
        />
        {!!oldPasswordError && <InputError>{oldPasswordError}</InputError>}
      </div>
      <div>
        <InputLabel>{t("changePassword.newPassword")}</InputLabel>
        <InputPassword
          name="newPassword"
          value={values.newPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          isError={!!newPasswordError}
        />
        {!!newPasswordError && <InputError>{newPasswordError}</InputError>}
      </div>
      <div>
        <InputLabel>{t("changePassword.confirmPassword")}</InputLabel>
        <InputPassword
          name="newPasswordConfirm"
          value={values.newPasswordConfirm}
          onChange={handleChange}
          onBlur={handleBlur}
          isError={!!newPasswordConfirmError}
        />
        {!!newPasswordConfirmError && (
          <InputError>{newPasswordConfirmError}</InputError>
        )}
      </div>
      {!!requirePasscode && (
        <div>
          <InputLabel>{t("changePassword.passCode")}</InputLabel>
          <Input
            name="passCode"
            maxLength={PASS_CODE_LENGTH}
            value={values.passCode}
            onChange={handleChange}
            onBlur={handleBlur}
            isError={!!passCodeError}
            rightAdornment={
              <button
                onClick={() => setIsPassCodeTooltipOpen(true)}
                className={styles.passCodeTooltip}
              >
                <InfoIcon width="20px" />
              </button>
            }
          />
          {!!passCodeError && <InputError>{passCodeError}</InputError>}
        </div>
      )}
      <PasswordRequirements password={values.newPassword} />
      <div className={styles.footer}>
        <Button
          onClick={handleSubmit}
          isDisabled={submit.isLoading}
          leftAdornment={
            submit.isLoading ? (
              <CircularProgress size="small" color="white" />
            ) : undefined
          }
        >
          {t("changePassword.submit")}
        </Button>
        <Button variant="text" onClick={resendOtp}>
          {t("changePassword.resendOtp")}
        </Button>
      </div>
      {isPassCodeTooltipOpen && (
        <ModalInfo isOpen onClose={() => setIsPassCodeTooltipOpen(false)}>
          {t("changePassword.passCodeMessage")}
        </ModalInfo>
      )}
    </div>
  );
}
